import { patientAbandonmentLogs } from '../services/Appointments/appointments'
import { config } from '../constants'

const sendAbandonmentOnUnload = (payload) => {
  try {
    const apiUrl = `${config.dev.baseURLV2}/tm2u_patient_abandonment/`
    const token = localStorage.getItem('access_token') || localStorage.getItem('refresh_token')
    const headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {})
    return true
  } catch (e) {
    console.warn('[Abandonment] sendAbandonmentOnUnload failed', e)
    return false
  }
}

export const markPageLoaded = () => {
  try {
    sessionStorage.setItem('page_was_loaded', 'true')
  } catch (e) {
    console.warn('Session init failed', e)
  }
}

// Helper function to validate if we have minimum required patient data for API
const hasMinimumPatientData = (patientData) => {
  if (!patientData) return false
  
  // Check if we have at least some identifying information
  const hasName = patientData.first_name || patientData.last_name
  const hasId = patientData.id || patientData.chart
  
  return hasName || hasId
}

// Helper function to create payload for API
const createAbandonmentPayload = (log) => {
  // Determine Appt_Type based on appType or fall back to is_registered
  let apptType = 'Initial'
  if (log.app_type === 'follow_up') {
    apptType = 'Follow-Up'
  } else if (log.app_type === 'initial') {
    apptType = 'Initial'
  }

  return {
    Session_Id: log.session_id,
    Reason: log.reason,
    Ref_Portal: 'CalOptimaV2',
    Patient_FName: log.patient_data?.first_name || '',
    Patient_LName: log.patient_data?.last_name || '',
    Chart_Num: log.patient_data?.chart || null,
    DoB: log.patient_data?.dob || '',
    State: log.patient_data?.state || '',
    ZipCode: log.patient_data?.zip_code || log.patient_data?.zipCode || log.patient_data?.zip || '',
    Specialty: log.specialty || log.care_type || '',
    Patient_Phone: log.patient_data?.phone || log.patient_data?.phone_number || '',
    Appt_Type: apptType,
    Appt_Req_Dt: log.selected_appt_date ? new Date(log.selected_appt_date).toISOString() : '', 
    Fin_Step: log.abandoned_at_step_name || '',
    Prov_Cnt: log.provider_data?.count || 0,
    Created_at: log.timestamp || new Date().toISOString(),
    email:log.patient_data?.email || '',
  }
}

export const trackAppointmentAbandonment = ({
  sessionId,
  reason,
  currentStep,
  stepName,
  userType,
  careType,
  language,
  patientData = null,
  providerData = null,
  specialty = null,
  isRegistered = false,
  appType = null,
  selectedApptDate = ''
}) => {
  try {
    if (currentStep >= 0 && currentStep < 1) {
      if (currentStep < 0.5 && !userType) {
        return
      }
    }

    if (currentStep >= 1 && currentStep < 2) {
      if (Math.floor(currentStep) === 1 && currentStep !== Math.floor(currentStep)) {
        // This is a modal, always track
      } else {
        // Main step 1 - only skip tracking if we have absolutely no patient data
        if (!hasMinimumPatientData(patientData) && reason !== 'page_refresh_or_browser_close') {
          return
        }
      }
    }

    if (currentStep >= 2 && currentStep < 3) {
      if (currentStep <= 2) {  // Inverted condition, removed empty block
        if (!careType && reason !== 'page_refresh_or_browser_close') {
          return
        }
      }
    }

    if (currentStep >= 3) {
      const isModalSubstep = currentStep !== Math.floor(currentStep) || currentStep === 3.6
      if (!isModalSubstep && (!careType || careType === '')) {
        return
      }
    }

    let detectedReason = reason
    if (reason === 'page_refresh_or_browser_close') {
      detectedReason = 'browser_close_or_navigation_away'
    }

    const abandonmentData = {
      session_id: sessionId,
      reason: detectedReason,
      abandoned_at_step: currentStep,
      abandoned_at_step_name: stepName,
      user_type: userType,
      care_type: careType,
      language,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      patient_data: patientData,
      provider_data: providerData,
      specialty: specialty,
      is_registered: isRegistered,
      app_type: appType,
      selected_appt_date: selectedApptDate,
    }

    const apiPayload = createAbandonmentPayload(abandonmentData)
    const sentImmediately = sendAbandonmentOnUnload(apiPayload)
    abandonmentData.sent_immediately = sentImmediately

    try {
      const existingLogs = localStorage.getItem('appointment_abandonment_logs')
      const logs = existingLogs ? JSON.parse(existingLogs) : []

      const isDuplicate = logs.some(log =>
        log.session_id === sessionId &&
        log.abandoned_at_step === currentStep &&
        log.reason === detectedReason
      )

      if (isDuplicate) {
        return
      }

      logs.push(abandonmentData)
      localStorage.setItem('appointment_abandonment_logs', JSON.stringify(logs))

    } catch (storageError) {
      console.warn('[Abandonment] Failed to store abandonment log', storageError)
    }
  } catch (e) {
    console.warn('[Abandonment] Tracking failed', e)
  }
}

export const trackPatientNotFound = async ({
  sessionId,
  userType,
  language,
  patientSearchData = null
}) => {
  const abandonmentData = {
    session_id: sessionId,
    reason: 'patient_not_found',
    abandoned_at_step: 0.5,
    abandoned_at_step_name: 'Patient Verification',
    user_type: userType || 'existing',
    care_type: '', // Patient hasn't selected care type yet
    language,
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    patient_data: patientSearchData || {},
    provider_data: null,
    specialty: '',
    is_registered: false
  }

  try {
    const existingLogs = localStorage.getItem('appointment_abandonment_logs')
    const logs = existingLogs ? JSON.parse(existingLogs) : []

    const isDuplicate = logs.some(log =>
      log.session_id === sessionId &&
      log.reason === 'patient_not_found'
    )

    if (!isDuplicate) {
      logs.push(abandonmentData)
      localStorage.setItem('appointment_abandonment_logs', JSON.stringify(logs))
    }
  } catch (storageError) {
    console.warn('[Patient Not Found] Failed to store in localStorage', storageError)
  }

  try {
    const payload = createAbandonmentPayload(abandonmentData)
    await patientAbandonmentLogs(payload)

    try {
      const logs = JSON.parse(localStorage.getItem('appointment_abandonment_logs') || '[]')
      const filteredLogs = logs.filter(log =>
        !(log.session_id === sessionId &&
          log.reason === 'patient_not_found')
      )
      localStorage.setItem('appointment_abandonment_logs', JSON.stringify(filteredLogs))
    } catch (cleanupError) {
      console.warn('[Patient Not Found] Failed to cleanup localStorage', cleanupError)
    }

  } catch (error) {
    console.error('[Patient Not Found] Failed to send to API immediately, will retry on next page load:', error)
  }
}

export const trackProviderNotFound = async ({
  sessionId,
  currentStep,
  stepName,
  userType,
  careType,
  language,
  patientData = null,
  specialty = null,
  isRegistered = false,
  appType = null
}) => {
  if (!hasMinimumPatientData(patientData)) {
    return
  }

  const abandonmentData = {
    session_id: sessionId,
    reason: 'provider_not_found',
    abandoned_at_step: currentStep,
    abandoned_at_step_name: stepName,
    user_type: userType,
    care_type: careType,
    language,
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    patient_data: patientData,
    provider_data: { count: 0 },
    specialty: specialty || '',
    is_registered: isRegistered,
    app_type: appType
  }

  try {
    const existingLogs = localStorage.getItem('appointment_abandonment_logs')
    const logs = existingLogs ? JSON.parse(existingLogs) : []
    
    const isDuplicate = logs.some(log => 
      log.session_id === sessionId &&
      log.abandoned_at_step === currentStep &&
      log.reason === 'provider_not_found'
    )
    
    if (!isDuplicate) {
      logs.push(abandonmentData)
      localStorage.setItem('appointment_abandonment_logs', JSON.stringify(logs))
    }
  } catch (storageError) {
    console.warn('[Provider Not Found] Failed to store in localStorage', storageError)
  }

  try {
    const payload = createAbandonmentPayload(abandonmentData)
    
    await patientAbandonmentLogs(payload)    
    try {
      const logs = JSON.parse(localStorage.getItem('appointment_abandonment_logs') || '[]')
      const filteredLogs = logs.filter(log => 
        !(log.session_id === sessionId && 
          log.reason === 'provider_not_found' && 
          log.abandoned_at_step === currentStep)
      )
      localStorage.setItem('appointment_abandonment_logs', JSON.stringify(filteredLogs))
    } catch (cleanupError) {
      console.warn('[Provider Not Found] Failed to cleanup localStorage', cleanupError)
    }
    
  } catch (error) {
    console.error('[Provider Not Found] Failed to send to API immediately, will retry on next page load:', error)
  }
}

export const checkAndUploadAbandonmentLogs = async () => {
  try {
    const logs = localStorage.getItem('appointment_abandonment_logs')
    if (!logs) return

    const abandonmentLogs = JSON.parse(logs)
    if (abandonmentLogs.length === 0) return

    // Determine if this was a page refresh or browser close
    const navigationType = performance.navigation?.type ||
                          performance.getEntriesByType('navigation')[0]?.type
    const wasRefreshed = sessionStorage.getItem('page_was_loaded')
    const isRefresh = navigationType === 1 || navigationType === 'reload' || wasRefreshed
    sessionStorage.removeItem('page_was_loaded')

    // Update reason for ALL logs with ambiguous reason, not just the last one
    for (const log of abandonmentLogs) {
      if (log.reason === 'browser_close_or_navigation_away') {
        log.reason = isRefresh ? 'page_refresh' : 'browser_close'
        log.detection_method = 'performance_api'
      }
    }

    // Remove duplicates and filter invalid logs before sending to API
    const uniqueLogs = []
    const seenKeys = new Set()

    for (const log of abandonmentLogs) {
      const step = log.abandoned_at_step
      const isModalSubstep = step !== Math.floor(step) || step === 3.6
      if (isModalSubstep) {
        // Modals are always valid, no filtering needed
      } else {

        if (step >= 3 && (!log.care_type || log.care_type === '')) {
          continue
        }

        // For Step 2 (Find Your Care), only require care type for non-refresh/close scenarios
        if (step === 2 && (!log.care_type || log.care_type === '') &&
            log.reason !== 'page_refresh' && log.reason !== 'browser_close') {
          continue
        }

        // For demographics step range (1-1.99), only skip if no patient data AND not a browser event
        if (step >= 1 && step < 2 &&
            !hasMinimumPatientData(log.patient_data) &&
            log.reason !== 'page_refresh' && log.reason !== 'browser_close') {
          continue
        }

        // For very early step (0), require at least user type
        if (step === 0 && !log.user_type) {
          continue
        }
      }

      const uniqueKey = `${log.session_id}-${log.abandoned_at_step}-${log.reason}`

      if (!seenKeys.has(uniqueKey)) {
        seenKeys.add(uniqueKey)
        uniqueLogs.push(log)
      }
    }

    // Send unique and valid logs to API, skipping those already sent during unload
    for (const log of uniqueLogs) {
      if (log.sent_immediately) {
        continue
      }

      if (!hasMinimumPatientData(log.patient_data)) {
        continue
      }

      try {
        const payload = createAbandonmentPayload(log)
        await patientAbandonmentLogs(payload)
      } catch (apiError) {
        console.error('[Abandonment Upload] Failed to send log to API:', apiError)
      }
    }

    localStorage.removeItem('appointment_abandonment_logs')
    localStorage.removeItem('last_abandonment_time')
  } catch (e) {
    console.warn('[Abandonment Upload] Failed to check abandonment logs', e)
  }
}